/**
 * Self-healing recovery for a 401 on an authenticated endpoint.
 *
 * ── Why this exists (2026-10-05 tx-presenter 401 alert) ──────────────────────────────────────────
 * On 2026-10-03 the AI endpoints (tx-presenter-service /generate-procedure-content, and
 * /categorize-diseases) were hardened to REQUIRE a Firebase Bearer token. The matching frontend
 * (which sends the token) shipped in the same release — but a dental front desk keeps a tab open for
 * days, so a tab still running the PRE-release bundle sends no token and the backend returns
 * 401 "Authorization header required". The call site swallowed that 401 (content load is
 * non-blocking), so NOTHING told the stale tab to update: it kept 401ing, silently degrading, and
 * tripping the prod watchdog on every call.
 *
 * The rule this encodes: a 401 on an endpoint we believe we are authenticated for means one of two
 * recoverable things, and we can tell them apart by the build version —
 *
 *   • The running bundle is STALE (its version != the server's version.json). The tab is old; the
 *     fix is to reload into the new bundle, which DOES send the token. We reuse the EXISTING
 *     stale-bundle path (versionCheck.reportStaleBundle → Layout's toast / quiet-reload), deduped by
 *     its own sessionStorage stamp so it can never reload-loop. This is the actual incident.
 *
 *   • The bundle is CURRENT but the Firebase token was null/expired (the SPA's localStorage
 *     `authenticated` session outlived the Firebase session). A silent token refresh lets the NEXT
 *     call carry a fresh token. Best-effort; if there is no Firebase user to refresh, nothing we can
 *     do here and the original error still propagates to the caller.
 *
 * The only destructive action (reload) is gated on PROVEN version drift, so a legitimate
 * "you are not allowed" 401 on a current bundle never triggers a reload — it just attempts a
 * harmless token refresh and lets the error through.
 *
 * ── Purity ───────────────────────────────────────────────────────────────────────────────────────
 * This file has ZERO imports on purpose: cypress/node-tests/stale-auth-recovery.test.js loads and
 * executes it via loadEsmUtil, which requires the suite to stay axios/firebase-free. All of the
 * environment (axios registration, firebase, version fetch, reportStaleBundle) is injected by the
 * thin wiring file src/plugins/staleAuthInterceptor.js. Keep it import-free.
 */

/**
 * Debounce the version probe. A single page action can fire several authed calls at once
 * (loadProcedureContent dispatches label + reason), and each would otherwise hit version.json.
 * One probe per window per this interval is plenty; the reload itself is separately deduped
 * downstream by reportStaleBundle's 5-minute sessionStorage stamp.
 */
const AUTH_PROBE_DEDUPE_MS = 30 * 1000;

let lastProbeAt = 0;

/** Test-only: reset the probe debounce between cases. */
function __resetProbeDebounce() {
  lastProbeAt = 0;
}

/**
 * Decide what to do about a (possibly) 401 axios error, and do it. Returns a short outcome string
 * for observability/testing. NEVER throws, NEVER swallows the original error (the interceptor
 * re-rejects regardless of what this returns).
 *
 * @param {object} error  the axios error (error.response.status is read)
 * @param {object} deps
 * @param {() => boolean}              deps.isAuthenticated     SPA session gate (localStorage authenticated)
 * @param {() => Promise<string|null>} deps.fetchServerVersion  resolves the server's build version
 * @param {string}                     deps.buildVersion        this bundle's build version
 * @param {() => Promise<any>}         deps.refreshToken        best-effort Firebase token refresh
 * @param {(reason: string) => any}    deps.onStale             the existing stale-bundle handler
 * @param {() => number}               [deps.now]               clock (injectable for tests)
 * @returns {Promise<string>} one of: ignored:not-401 | ignored:not-authenticated | ignored:deduped |
 *                            ignored:version-fetch-failed | stale-reload | token-refresh
 */
async function handleAuthError(error, deps) {
  const status = error && error.response && error.response.status;
  if (status !== 401) return 'ignored:not-401';
  if (!deps.isAuthenticated()) return 'ignored:not-authenticated';

  const now = deps.now ? deps.now() : Date.now();
  if (now - lastProbeAt < AUTH_PROBE_DEDUPE_MS) return 'ignored:deduped';
  lastProbeAt = now;

  let serverVersion = null;
  try {
    serverVersion = await deps.fetchServerVersion();
  } catch (_e) {
    return 'ignored:version-fetch-failed';
  }

  if (serverVersion && deps.buildVersion && serverVersion !== deps.buildVersion) {
    deps.onStale(`401 on an authed endpoint; running bundle ${deps.buildVersion} != server ${serverVersion}`);
    return 'stale-reload';
  }

  // Bundle is current (or we can't prove drift) → the token, not the bundle, is the problem.
  try {
    await deps.refreshToken();
  } catch (_e) {
    /* best effort — nothing more we can safely do from here */
  }
  return 'token-refresh';
}

export { handleAuthError, __resetProbeDebounce, AUTH_PROBE_DEDUPE_MS };
